import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

export type ThemeMode = 'system' | 'light' | 'dark';

export function useTheme() {
  const [themeMode, setThemeMode] = useState<ThemeMode>(() => {
    try {
      const saved = localStorage.getItem('digestlog_theme');
      if (saved === 'light' || saved === 'dark' || saved === 'system') return saved;
    } catch {}
    return 'system';
  });

  const [effectiveTheme, setEffectiveTheme] = useState<'light' | 'dark'>('dark');

  useEffect(() => {
    const applyTheme = () => {
      let isDark = true;
      if (themeMode === 'system') {
        isDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
      } else {
        isDark = themeMode === 'dark';
      }

      setEffectiveTheme(isDark ? 'dark' : 'light');

      const root = document.documentElement;
      const body = document.body;
      if (isDark) {
        root.classList.add('dark');
        root.classList.remove('light');
        root.style.colorScheme = 'dark';
        root.style.backgroundColor = '#090a0f';
        if (body) {
          body.classList.add('dark');
          body.classList.remove('light');
          body.style.backgroundColor = '#090a0f';
        }
      } else {
        root.classList.remove('dark');
        root.classList.add('light');
        root.style.colorScheme = 'light';
        root.style.backgroundColor = '#f8fafc';
        if (body) {
          body.classList.remove('dark');
          body.classList.add('light');
          body.style.backgroundColor = '#f8fafc';
        }
      }

      // Update meta theme-color for iOS safari status bar & browser chrome
      const themeColorMetas = document.querySelectorAll('meta[name="theme-color"]');
      themeColorMetas.forEach((meta) => {
        meta.setAttribute('content', isDark ? '#090a0f' : '#f8fafc');
      });

      const appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]');
      if (appleStatusBarMeta) {
        appleStatusBarMeta.setAttribute('content', 'black-translucent');
      }

      if (Capacitor.isNativePlatform()) {
        StatusBar.setStyle({ style: isDark ? Style.Dark : Style.Light }).catch(() => {});
      }
    };

    applyTheme();

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (themeMode === 'system') {
        applyTheme();
      }
    };

    const handleCustomChange = (e: Event) => {
      const customEvent = e as CustomEvent<ThemeMode>;
      if (customEvent.detail && customEvent.detail !== themeMode) {
        setThemeMode(customEvent.detail);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    window.addEventListener('digestlog_theme_change', handleCustomChange);
    return () => {
      mediaQuery.removeEventListener('change', handleChange);
      window.removeEventListener('digestlog_theme_change', handleCustomChange);
    };
  }, [themeMode]);

  const updateTheme = (mode: ThemeMode) => {
    setThemeMode(mode);
    try {
      localStorage.setItem('digestlog_theme', mode);
      window.dispatchEvent(new CustomEvent('digestlog_theme_change', { detail: mode }));
    } catch {}
  };

  return {
    themeMode,
    effectiveTheme,
    updateTheme,
  };
}
